/**
 * Menu Manage — Pure utility functions.
 */

import type { MenuItem, MenuItemOption, MenuCategory, OptionGroup, LibraryModifier } from './types';

/** Generate a unique ID (same logic used throughout the module). */
export function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Generate a UUID for entities that must never retain a source library identity. */
export function genUuid(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

/**
 * Generate a relational modifier Group Code based on [BRAND]-MOD-[sequence].
 * Scans existing groups across the profile to determine the next available number.
 */
export function generateGroupCode(brandName: string, existingCategories: MenuCategory[]): string {
  const prefix = (brandName || 'BRAND').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 5);
  const allCodes: string[] = [];
  for (const cat of existingCategories) {
    for (const item of cat.items) {
      for (const grp of (item.optionGroups || [])) {
        if (grp.groupCode) allCodes.push(grp.groupCode);
      }
    }
  }
  // Find the highest existing sequence number for this brand prefix
  const pattern = new RegExp(`^${prefix}-MOD-(\\d+)$`, 'i');
  let maxSeq = 0;
  for (const code of allCodes) {
    const m = code.match(pattern);
    if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10));
  }
  return `${prefix}-MOD-${String(maxSeq + 1).padStart(3, '0')}`;
}

/**
 * Generate a category code: [BRAND]-CAT-NNN.
 */
export function generateCategoryCode(brandName: string, existingCategories: MenuCategory[]): string {
  const prefix = (brandName || 'BRAND').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 5);
  const pattern = new RegExp(`^${prefix}-CAT-(\\d+)$`, 'i');
  let maxSeq = 0;
  for (const cat of existingCategories) {
    if (cat.code) {
      const m = cat.code.match(pattern);
      if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10));
    }
  }
  return `${prefix}-CAT-${String(maxSeq + 1).padStart(3, '0')}`;
}

/**
 * Generate an item code: [BRAND]-ITM-NNN.
 */
export function generateItemCode(brandName: string, existingCategories: MenuCategory[]): string {
  const prefix = (brandName || 'BRAND').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 5);
  const pattern = new RegExp(`^${prefix}-ITM-(\\d+)$`, 'i');
  let maxSeq = 0;
  for (const cat of existingCategories) {
    for (const item of cat.items) {
      if (item.code) {
        const m = item.code.match(pattern);
        if (m) maxSeq = Math.max(maxSeq, parseInt(m[1], 10));
      }
    }
  }
  return `${prefix}-ITM-${String(maxSeq + 1).padStart(3, '0')}`;
}

/** Deep-clone a MenuItemOption with a fresh ID and " - Copy" suffix. */
export function duplicateOption(opt: MenuItemOption): MenuItemOption {
  return { ...opt, id: genId(), nameEN: `${opt.nameEN} - Copy` };
}

/** Deep-clone an OptionGroup with fresh IDs on itself and all nested options. */
export function duplicateGroup(grp: OptionGroup): OptionGroup {
  return {
    ...grp,
    id: genId(),
    nameEN: `${grp.nameEN} - Copy`,
    linkedLibraryId: undefined, // paste creates an independent copy
    options: grp.options.map(o => ({ ...o, id: genId() })),
  };
}

/**
 * Deep-clone a MenuItem with fresh IDs on itself and all nested groups/options.
 * Strips connectedModules, systemManaged, and campaignMeta so the copy is
 * a clean, unlinked local item — prevents ghost edits and module contention.
 */
export function duplicateItem(item: MenuItem): MenuItem {
  const newGroups: OptionGroup[] = (item.optionGroups || []).map(grp => ({
    ...grp,
    id: genId(),
    linkedLibraryId: undefined, // break library link on deep clone
    options: grp.options.map(o => ({ ...o, id: genId(), libraryOptionId: undefined })),
  }));
  return {
    ...item,
    id: genId(),
    nameEN: `${item.nameEN} - Copy`,
    options: item.options.map(o => ({ ...o, id: genId() })),
    optionGroups: newGroups,
    categoryIds: undefined,
    systemManaged: undefined,
    campaignMeta: undefined,
  };
}

export function deepDecoupledCopyItem(item: MenuItem, opts: { categoryIds?: string[]; sortOrder?: number; preserveName?: boolean } = {}): MenuItem {
  const newGroups: OptionGroup[] = (item.optionGroups || []).map(grp => {
    const groupId = genId();
    return {
      ...grp,
      id: groupId,
      linkedLibraryId: undefined,
      options: (grp.options || []).map(o => ({ ...o, id: genId(), libraryOptionId: undefined })),
    };
  });
  return {
    ...item,
    id: genId(),
    nameEN: opts.preserveName ? item.nameEN : `${item.nameEN} - Copy`,
    options: (item.options || []).map(o => ({ ...o, id: genId(), libraryOptionId: undefined })),
    optionGroups: newGroups,
    optionGroupIds: newGroups.map(g => g.id),
    categoryIds: opts.categoryIds,
    sortOrder: opts.sortOrder ?? item.sortOrder,
    systemManaged: undefined,
    campaignMeta: undefined,
  };
}

export function deepDecoupledCopyCategory(category: MenuCategory, opts: { sortOrder?: number; preserveName?: boolean } = {}): MenuCategory {
  const categoryId = genId();
  return {
    ...category,
    id: categoryId,
    nameEN: opts.preserveName ? category.nameEN : `${category.nameEN} - Copy`,
    sortOrder: opts.sortOrder ?? category.sortOrder,
    items: (category.items || []).map((item, index) => deepDecoupledCopyItem(item, {
      categoryIds: [categoryId],
      sortOrder: index,
      preserveName: true,
    })),
  };
}

/**
 * Check whether a set of brand IDs falls within a user's allowed brands.
 * Returns true (unrestricted) when userBrands is undefined or empty.
 */
export function isWithinBrandScope(
  userBrands: string[] | undefined,
  fileBrandIds: string[],
): boolean {
  if (!userBrands || userBrands.length === 0) return true;
  if (fileBrandIds.length === 0) return true; // unscoped files visible to all
  return fileBrandIds.some(bid => userBrands.includes(bid));
}

/** Custom event name dispatched after any menu data mutation. */
export const MENU_DATA_UPDATED_EVENT = 'menu-data-updated';

// ─── Unassigned Bucket (hidden container for removed option groups) ─────────

/** Well-known ID for the hidden "Unassigned" category. */
export const UNASSIGNED_CAT_ID = '__unassigned__';
/** Well-known ID for the hidden placeholder item inside the Unassigned category. */
export const UNASSIGNED_ITEM_ID = '__unassigned_item__';

/**
 * Ensure the profile has the hidden Unassigned category + item.
 * Returns a new categories array with the bucket appended if it didn't exist.
 */
export function ensureUnassignedBucket(categories: MenuCategory[]): MenuCategory[] {
  if (categories.some(c => c.id === UNASSIGNED_CAT_ID)) return categories;
  const bucket: MenuCategory = {
    id: UNASSIGNED_CAT_ID,
    nameEN: 'Unassigned Modifiers',
    nameAR: 'معدّلات غير مخصصة',
    sortOrder: 99999,
    hidden: true,
    items: [{
      id: UNASSIGNED_ITEM_ID,
      nameEN: 'Unassigned',
      nameAR: 'غير مخصص',
      basePrice: 0,
      options: [],
      optionGroups: [],
      sortOrder: 0,
      hidden: true,
    }],
  };
  return [...categories, bucket];
}

/** Check if a category ID is the system Unassigned bucket. */
export function isUnassignedCat(catId: string): boolean {
  return catId === UNASSIGNED_CAT_ID;
}

// ─── Factory: Clean Slate Group/Option Creation ─────────────────────────────

/** Create a brand-new, completely unlinked OptionGroup (Clean Slate). */
export function createCleanOptionGroup(): OptionGroup {
  return {
    id: genId(),
    nameEN: '',
    nameAR: '',
    options: [],
    minSelection: 0,
    maxSelection: 0,
    linkedLibraryId: undefined,
    hidden: false,
  };
}

/** Create a brand-new, empty MenuItemOption with a fresh ID. */
export function createCleanOption(): MenuItemOption {
  return { id: genId(), nameEN: '', nameAR: '', extraPrice: 0 };
}

/**
 * Deep-clone an OptionGroup, giving fresh IDs to the group and all options.
 * This breaks ALL references to the source so edits cannot leak.
 */
export function deepCloneGroup(grp: OptionGroup): OptionGroup {
  return {
    ...grp,
    id: genId(),
    options: grp.options.map(o => ({ ...o, id: genId() })),
    linkedLibraryId: grp.linkedLibraryId, // preserve library link if cloned from library
  };
}

/**
 * Deep-clone an entire item's optionGroups array, giving fresh option IDs
 * while preserving group IDs (used to break shared references after spread).
 */
export function deepCloneItemGroups(groups: OptionGroup[]): OptionGroup[] {
  return groups.map(g => ({
    ...g,
    options: g.options.map(o => ({ ...o })),
  }));
}

// ─── Data Integrity: Duplicate ID Deduplication ─────────────────────────────

/**
 * Scan all items and option groups in a profile's categories.
 * If any group/option ID appears more than once, regenerate a fresh ID
 * for every occurrence after the first. Returns a new categories array.
 *
 * Shared groups (same groupId intentionally on multiple items) are preserved:
 * their IDs and nested option IDs are NOT regenerated.
 */
export function deduplicateIds(categories: MenuCategory[]): { categories: MenuCategory[]; fixed: number } {
  // Phase 1: Identify intentionally shared group IDs (same groupId on different items)
  const groupOwner = new Map<string, string>();
  const sharedGrpIds = new Set<string>();
  for (const cat of categories) {
    for (const item of cat.items) {
      for (const grp of (item.optionGroups || [])) {
        const owner = groupOwner.get(grp.id);
        if (owner && owner !== item.id) {
          sharedGrpIds.add(grp.id);
        } else if (!owner) {
          groupOwner.set(grp.id, item.id);
        }
      }
    }
  }

  // Phase 2: Dedup IDs, preserving shared groups
  const seen = new Set<string>();
  let fixed = 0;

  const dedup = (id: string): string => {
    if (seen.has(id)) { fixed++; return genId(); }
    seen.add(id);
    return id;
  };

  const result = categories.map(cat => ({
    ...cat,
    items: cat.items.map(item => ({
      ...item,
      id: dedup(item.id),
      optionGroups: (item.optionGroups || []).map(grp => {
        if (sharedGrpIds.has(grp.id)) {
          // Shared group — preserve all IDs, mark as seen to catch unrelated collisions
          seen.add(grp.id);
          grp.options.forEach(o => seen.add(o.id));
          return { ...grp, options: grp.options.map(o => ({ ...o })) };
        }
        return {
          ...grp,
          id: dedup(grp.id),
          options: grp.options.map(opt => ({
            ...opt,
            id: dedup(opt.id),
          })),
        };
      }),
    })),
  }));

  return { categories: result, fixed };
}

// ─── Multi-Category & Bulk Modifier Service Helpers ─────────────────────────

/**
 * Update which categories an item belongs to.
 * Moves the canonical item record to the primary category and adds
 * reference links (via categoryIds) for all additional categories.
 * Returns a new categories array with the item placed/removed accordingly.
 */
export function updateItemCategories(
  categories: MenuCategory[],
  itemId: string,
  primaryCatId: string,
  linkedCatIds: string[],
): MenuCategory[] {
  // 1. Find existing item across all categories
  let item: MenuItem | null = null;
  for (const cat of categories) {
    const found = cat.items.find(it => it.id === itemId);
    if (found) { item = found; break; }
  }
  if (!item) return categories;

  const allCatIds = [primaryCatId, ...linkedCatIds.filter(id => id !== primaryCatId)];
  const updatedItem: MenuItem = { ...item, categoryIds: allCatIds };

  return categories.map(cat => {
    const shouldContain = allCatIds.includes(cat.id);
    const hasItem = cat.items.some(it => it.id === itemId);

    if (shouldContain && hasItem) {
      // Update in place
      return { ...cat, items: cat.items.map(it => it.id === itemId ? updatedItem : it) };
    }
    if (shouldContain && !hasItem) {
      // Add to this category
      return { ...cat, items: [...cat.items, { ...updatedItem, sortOrder: cat.items.length }] };
    }
    if (!shouldContain && hasItem) {
      // Remove from this category
      return { ...cat, items: cat.items.filter(it => it.id !== itemId) };
    }
    return cat;
  });
}

/**
 * Link multiple library modifiers to an item via relational IDs.
 * NO CLONING — just push modifier IDs into item.optionGroupIds.
 * The optionGroups array is hydrated for display from the library map.
 */
export function linkBulkModifiers(
  prodForm: MenuItem,
  mods: LibraryModifier[],
  libMap?: Map<string, LibraryModifier>,
): { updated: MenuItem; linked: string[]; skipped: string[] } {
  const linked: string[] = [];
  const skipped: string[] = [];
  const ids = [...(prodForm.optionGroupIds || [])];

  for (const mod of mods) {
    if (ids.includes(mod.id)) { skipped.push(mod.nameEN); continue; }
    // Also skip if name matches an existing linked group
    const existingNames = ids.map(id => libMap?.get(id)?.nameEN?.trim().toLowerCase()).filter(Boolean);
    if (existingNames.includes(mod.nameEN.trim().toLowerCase())) { skipped.push(mod.nameEN); continue; }
    ids.push(mod.id);
    linked.push(mod.nameEN);
  }

  // Build hydrated optionGroups from the new IDs list
  const resolveMap = libMap || new Map(mods.map(m => [m.id, m]));
  const hydrated = hydrateOptionGroups(ids, resolveMap);

  return { updated: { ...prodForm, optionGroupIds: ids, optionGroups: hydrated }, linked, skipped };
}

// ─── Hydration Utilities (Relational Integrity) ─────────────────────────────

/**
 * Hydrate OptionGroup[] from an array of library modifier IDs.
 * This is the ONLY way to convert IDs → display-ready group objects.
 * The library Map is the single source of truth.
 */
export function hydrateOptionGroups(
  ids: string[],
  libMap: Map<string, LibraryModifier>,
): OptionGroup[] {
  return ids.map(id => {
    const mod = libMap.get(id);
    if (!mod) return null;
    return {
      id: mod.id,
      nameEN: mod.nameEN,
      nameAR: mod.nameAR,
      options: mod.options.map(o => ({
        id: o.id,
        nameEN: o.nameEN,
        nameAR: o.nameAR,
        extraPrice: o.extraPrice,
        hidden: o.hidden,
        libraryOptionId: o.id,
        pinned: (o as any).pinned,
      })),
      minSelection: mod.minSelection,
      maxSelection: mod.maxSelection,
      linkedLibraryId: mod.id,
      hidden: false,
      allowMultipleQty: false,
      groupCode: undefined,
    } as OptionGroup;
  }).filter(Boolean) as OptionGroup[];
}

/**
 * Extract optionGroupIds from an item's optionGroups (migration helper).
 * Used to convert legacy embedded groups into relational IDs.
 * Groups WITH linkedLibraryId → use that ID directly.
 * Groups WITHOUT linkedLibraryId → these are "inline" groups that
 * must first be promoted to the library before extraction.
 */
export function extractOptionGroupIds(item: MenuItem): string[] {
  if (item.optionGroupIds && item.optionGroupIds.length > 0) return item.optionGroupIds;
  return (item.optionGroups || [])
    .map(g => g.linkedLibraryId || g.id)
    .filter(Boolean);
}

/**
 * Count how many items across all categories reference a given library ID.
 */
export function countCategoryLinkedProducts(libId: string, categories: MenuCategory[]): number {
  let count = 0;
  for (const cat of categories) {
    for (const item of cat.items) {
      if ((item.optionGroupIds || []).includes(libId) ||
          (item.optionGroups || []).some(g => g.linkedLibraryId === libId)) {
        count++;
      }
    }
  }
  return count;
}

// ─── Shared Group Utilities (Relational Integrity) ──────────────────────────

/**
 * Smart-copy an item: fresh ID/name for the item, but SAME group references.
 * This enables true relational sharing — editing a shared group on any item
 * propagates to every other item that references the same groupId.
 */
export function smartCopyItem(item: MenuItem): MenuItem {
  return {
    ...item,
    id: genId(),
    nameEN: `${item.nameEN} - Copy`,
    options: [],
    // Keep SAME optionGroupIds (relational pointers — shared by reference)
    optionGroupIds: [...(item.optionGroupIds || [])],
    // Hydrated view — copied for display continuity
    optionGroups: (item.optionGroups || []).map(g => ({ ...g, options: g.options.map(o => ({ ...o })) })),
    categoryIds: undefined,
    systemManaged: undefined,
    campaignMeta: undefined,
  };
}

/**
 * After editing a group, propagate the change to ALL items that share the same
 * group ID — OR the same linkedLibraryId — across the entire profile.
 * This is the core of relational integrity for the Master Record pattern.
 */
export function propagateGroupEdit(categories: MenuCategory[], editedGroup: OptionGroup): MenuCategory[] {
  const libId = editedGroup.linkedLibraryId;
  return categories.map(cat => ({
    ...cat,
    items: cat.items.map(item => ({
      ...item,
      optionGroups: (item.optionGroups || []).map(grp => {
        // Match by same group ID (shared via bulk assign)
        if (grp.id === editedGroup.id) {
          return { ...editedGroup, options: editedGroup.options.map(o => ({ ...o })) };
        }
        // Match by same linkedLibraryId (shared via Library linking)
        if (libId && grp.linkedLibraryId === libId) {
          return {
            ...grp,
            nameEN: editedGroup.nameEN,
            nameAR: editedGroup.nameAR,
            minSelection: editedGroup.minSelection,
            maxSelection: editedGroup.maxSelection,
            hidden: editedGroup.hidden,
            allowMultipleQty: editedGroup.allowMultipleQty,
            groupCode: editedGroup.groupCode,
            options: editedGroup.options.map(o => ({ ...o })),
          };
        }
        return grp;
      }),
    })),
  }));
}

/**
 * Check if a group is shared across more than one item in the profile.
 * Checks both optionGroupIds (relational) and optionGroups (hydrated fallback).
 */
export function isSharedGroup(groupId: string, categories: MenuCategory[], linkedLibraryId?: string): boolean {
  let count = 0;
  for (const cat of categories) {
    for (const item of cat.items) {
      const matchById = linkedLibraryId
        ? (item.optionGroupIds || []).includes(linkedLibraryId)
        : (item.optionGroupIds || []).includes(groupId);
      const matchByGroups = (item.optionGroups || []).some(g =>
        g.id === groupId || (linkedLibraryId && g.linkedLibraryId === linkedLibraryId)
      );
      if (matchById || matchByGroups) {
        count++;
        if (count > 1) return true;
      }
    }
  }
  return false;
}

/**
 * Detach / break a shared group: create a completely independent copy
 * with fresh IDs for the group and all its options. This is the explicit
 * "✂️ Break Link & Make Local" action.
 */
export function detachGroupCopy(grp: OptionGroup): OptionGroup {
  return {
    ...grp,
    id: genUuid(),
    linkedLibraryId: undefined,
    localOnly: true,
    options: grp.options.map(o => ({ ...o, id: genUuid(), libraryOptionId: undefined })),
  };
}

/** Replace one item-level group reference without mutating the source library group. */
export function replaceOptionGroupReference(
  item: MenuItem,
  source: OptionGroup,
  replacement: OptionGroup,
): MenuItem {
  const sourceIds = new Set([source.id, source.linkedLibraryId].filter(Boolean) as string[]);
  const currentIds = item.optionGroupIds?.length
    ? item.optionGroupIds
    : (item.optionGroups || []).map(group => group.linkedLibraryId || group.id);
  let inserted = false;
  const optionGroupIds = currentIds.flatMap(id => {
    if (!sourceIds.has(id)) return [id];
    if (inserted) return [];
    inserted = true;
    return [replacement.id];
  });
  if (!inserted) optionGroupIds.push(replacement.id);

  return {
    ...item,
    optionGroupIds,
    optionGroups: (item.optionGroups || []).map(group =>
      group.id === source.id ? replacement : group,
    ),
  };
}

/** Remove an item-level reference only; no library or group records are deleted. */
export function removeOptionGroupReference(item: MenuItem, source: OptionGroup): MenuItem {
  const sourceIds = new Set([source.id, source.linkedLibraryId].filter(Boolean) as string[]);
  return {
    ...item,
    optionGroupIds: (item.optionGroupIds || []).filter(id => !sourceIds.has(id)),
    optionGroups: (item.optionGroups || []).filter(group => group.id !== source.id),
  };
}
